import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import React, { useState, useEffect } from "react";
import { View, TouchableOpacity, ScrollView, SafeAreaView } from "react-native";
import tw from "twrnc";

import { DSText } from "../../design-system/components/DSText";
import { spacing, radius, shadows } from "../../design-system/tokens";
import { resolveGroupContextMeta } from "../../features/multi-player/utils/groupMeta";
import { useLanguageStore } from "../../features/quiz-core/languageStore";
import { t } from "../../i18n/translations";
import type { RootStackParamList } from '../../navigation/types';
import { supabase } from "../services/supabaseService";

import { iqesColors } from "./iqesDesignSystem";

// Unified props for Solo, Multiplayer, and Challenge modes
interface UnifiedTestamentScreenProps {
  mode?: 'solo' | 'multiplayer' | 'challenge';
  opponent?: { id: string; name: string }; // For Challenge mode
}

type ChallengeOpponent = { id: string; name: string };

type TestamentRouteParams = {
  category?: string;
  categoryId?: number;
  initialTestament?: 'old' | 'new' | 'early_church' | 'church_traditions';
  challengeMode?: boolean;
  rematch?: boolean;
  oldRoomId?: string;
  familyId?: string;
  familyName?: string;
  friendsGroupId?: string;
  friendsGroupName?: string;
  opponent?: ChallengeOpponent;
};

type SubcategoryRow = {
  category_id: number;
  name: string;
  description?: string | null;
  categories?: {
    parent_category_id?: number | null;
  } | null;
};

type TestamentScreenNavigationProp = StackNavigationProp<
  RootStackParamList,
  "Testament" | "MultiplayerTestament" | "ChallengeTestament"
>;

type TestamentScreenRouteProp = RouteProp<
  RootStackParamList,
  "Testament" | "MultiplayerTestament" | "ChallengeTestament"
>;

/**
 * UnifiedTestamentScreen - Proof of Concept
 *
 * This component consolidates Solo Play, Multiplayer, and Challenge Testament screens,
 * reducing code duplication by ~90% while maintaining all functionality.
 *
 * Key features:
 * - Mode detection from route name or explicit prop ('solo' | 'multiplayer' | 'challenge')
 * - Shared UI rendering logic
 * - Mode-specific navigation handling (Solo/Multiplayer/Challenge flows)
 * - Challenge mode support with opponent parameter
 * - Full internationalization support
 * - Maintains all existing animations and styling
 */
const UnifiedTestamentScreen: React.FC<UnifiedTestamentScreenProps> = ({ mode: explicitMode, opponent }) => {
  const navigation = useNavigation<TestamentScreenNavigationProp>();
  const route = useRoute<TestamentScreenRouteProp>();
  const { language: userLang } = useLanguageStore();

  // Determine mode from route name if not explicitly provided
  const mode = explicitMode ||
    (route.name === 'MultiplayerTestament' ? 'multiplayer' :
     route.name === 'ChallengeTestament' ? 'challenge' : 'solo');

  // Extract params - safely handle union types
  const params = (route.params || {}) as TestamentRouteParams;
  const category = params.category;
  const categoryId = params.categoryId;
  const initialTestament = params.initialTestament;
  const challengeMode = params.challengeMode;
  // Multiplayer-specific params
  const rematch = params.rematch;
  const oldRoomId = params.oldRoomId;
  const familyId = params.familyId;
  const familyName = params.familyName;
  const friendsGroupId = params.friendsGroupId;
  const friendsGroupName = params.friendsGroupName;
  // Challenge-specific params
  const routeOpponent = params.opponent;
  const groupMeta = resolveGroupContextMeta(
    {
      familyId,
      familyName,
      friendsGroupId,
      friendsGroupName,
    },
    userLang,
  );

  const challengeOpponent = opponent || routeOpponent;

  // State for church history subcategories
  const [subcategories, setSubcategories] = useState<SubcategoryRow[]>([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);

  // Check if this is a church history category
  const isChurchHistory = initialTestament === 'early_church' || initialTestament === 'church_traditions';

  /**
   * Fetch subcategories for church history categories
   */
  useEffect(() => {
    async function fetchSubcategories() {
      if (!isChurchHistory || !categoryId) return;

      setLoadingSubcategories(true);

      try {
        const { data, error } = await supabase
          .from('category_translations')
          .select(`
            category_id,
            name,
            description,
            language_code,
            categories:category_id (
              icon,
              parent_category_id
            )
          `)
          .eq('language_code', userLang);

        if (error) {
          setLoadingSubcategories(false);
          return;
        }

        // Filter for subcategories of this parent category and sort by category_id
        const rows = (data || []) as SubcategoryRow[];
        const subs = rows.filter((cat) =>
          cat.categories?.parent_category_id === categoryId
        ).sort((a, b) => a.category_id - b.category_id) || [];

        setSubcategories(subs);
      } catch (_err) {
        // Silently handle error
      }

      setLoadingSubcategories(false);
    }

    fetchSubcategories();
  }, [categoryId, userLang, isChurchHistory]);

  /**
   * Handle subcategory selection for church history
   */
  const handleSubcategorySelect = (subcategoryId: number, subcategoryName: string) => {
    // Navigate to difficulty screen with the subcategory
    if (mode === 'multiplayer') {
      navigation.navigate("MultiplayerDifficulty", {
        category: subcategoryName,
        categoryId: subcategoryId,
        rematch,
        oldRoomId,
        familyId,
        familyName,
        friendsGroupId,
        friendsGroupName,
      });
    } else if (mode === 'challenge') {
      navigation.navigate("ChallengeDifficulty", {
        opponent: challengeOpponent,
        categoryId: subcategoryId,
        category: subcategoryName,
        testament: null,
      });
    } else {
      navigation.navigate("Difficulty", {
        category: subcategoryName,
        categoryId: subcategoryId,
        challengeMode
      });
    }
  };

  /**
   * Handle General mode selection
   * Routes to appropriate difficulty screen based on mode
   */
  const handleGeneralMode = () => {
    if (mode === 'multiplayer') {
      navigation.navigate("MultiplayerDifficulty", {
        category,
        categoryId,
        rematch,
        oldRoomId,
        familyId,
        familyName,
        friendsGroupId,
        friendsGroupName,
      });
    } else if (mode === 'challenge') {
      navigation.navigate("ChallengeDifficulty", {
        opponent: challengeOpponent,
        categoryId,
        category,
        testament: null, // No testament specified for general mode
      });
    } else {
      navigation.navigate("Difficulty", {
        category,
        categoryId,
        challengeMode
      });
    }
  };

  /**
   * Handle Books of the Bible mode selection
   * Routes to appropriate book selection screen based on mode
   */
  const handleBooksMode = () => {
    // Determine testament based on category or initialTestament
    let testamentValue: "old" | "new" = initialTestament || "new";

    if (category) {
      const categoryLower = category.toLowerCase();
      const oldTestamentKeywords = ['old', 'ancien', 'antiguo', 'altes', 'antigo', 'ветхий', 'lumang', 'antico', 'ብሉይ', '구약'];
      const isOldTestament = oldTestamentKeywords.some(keyword =>
        categoryLower.includes(keyword.toLowerCase())
      );
      if (isOldTestament) {
        testamentValue = "old";
      }
    }

    if (mode === 'multiplayer') {
      navigation.navigate("MultiplayerBookSelection", {
        category,
        categoryId,
        testament: testamentValue,
        rematch,
        oldRoomId,
        familyId,
        familyName,
        friendsGroupId,
        friendsGroupName,
      });
    } else if (mode === 'challenge') {
      navigation.navigate("ChallengeBookSelection", {
        opponent: challengeOpponent,
        categoryId,
        category,
        testament: testamentValue,
      });
    } else {
      navigation.navigate("BookSelection", {
        category,
        testament: testamentValue,
        challengeMode
      });
    }
  };

  return (
    <View style={[tw`flex-1`, { backgroundColor: iqesColors.canvas }]}>

      <SafeAreaView style={tw`flex-1`}>
        <ScrollView
          style={tw`flex-1`}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
        >
          <ScrollView
            horizontal
            contentContainerStyle={{ minWidth: '100%' }}
            showsHorizontalScrollIndicator={false}
            scrollEnabled={true}
          >
            <View style={{ width: '100%', minWidth: 350 }}>
              {/* Premium Header Section */}
              <View style={{
                alignItems: 'center',
                paddingTop: spacing['4xl'],
                paddingBottom: spacing.xl,
                paddingHorizontal: spacing['2xl'],
                borderBottomWidth: 1,
                borderBottomColor: 'rgba(246,190,58,0.28)',
              }}>
                {/* Back Button */}
                <View style={{
                  position: 'absolute',
                  left: spacing['2xl'],
                  top: spacing.xl,
                  zIndex: 10,
                }}>
                  <TouchableOpacity
                    onPress={() => navigation.goBack()}
                    style={{
                      width: spacing['4xl'],
                      height: spacing['4xl'],
                      borderRadius: radius.full,
                      backgroundColor: 'rgba(8,10,24,0.88)',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderWidth: 1,
                      borderColor: 'rgba(246,190,58,0.28)',
                    }}
                  >
                    <Ionicons name="arrow-back" size={20} color={iqesColors.goldBright} />
                  </TouchableOpacity>
                </View>

                {/* Journey Badge */}
                <View style={{
                  backgroundColor: 'rgba(76,29,120,0.72)',
                  paddingHorizontal: spacing.lg + 2,
                  paddingVertical: spacing.sm + 2,
                  borderRadius: radius.full,
                  marginBottom: spacing.lg,
                  borderWidth: 1,
                  borderColor: 'rgba(246,190,58,0.4)',
                }}>
                  <DSText
                    variant="label"
                    color={iqesColors.goldSoft}
                    align="center"
                    style={{
                      fontWeight: '700',
                    }}
                  >
                    {mode === 'multiplayer'
                      ? t('multiplayer_lobby.setupTestamentBadge', userLang)
                      : t('begin_journey', userLang)}
                  </DSText>
                </View>

                {/* Main Title */}
                <DSText
                  variant="display"
                  color={iqesColors.ivory}
                  align="center"
                  style={{
                    fontSize: mode === 'multiplayer' ? 28 : 32,
                    fontWeight: '800',
                    marginBottom: spacing.sm,
                    letterSpacing: 0,
                    lineHeight: mode === 'multiplayer' ? 34 : 38,
                    paddingHorizontal: mode === 'multiplayer' ? spacing.lg : 0,
                  }}
                >
                  {mode === 'multiplayer'
                    ? t('multiplayer_lobby.setupTestamentTitle', userLang)
                    : t('choose_your_path', userLang)}
                </DSText>

                {/* Subtitle */}
                <DSText
                  variant="body"
                  color={'rgba(255,248,232,0.72)'}
                  align="center"
                  style={{
                    fontWeight: '500',
                    paddingHorizontal: mode === 'multiplayer' ? spacing.xl : spacing['3xl'],
                    maxWidth: 360,
                  }}
                >
                  {mode === 'multiplayer'
                    ? t('multiplayer_lobby.setupTestamentSubtitle', userLang).replace('{{groupType}}', groupMeta.groupLabelLower)
                    : t('explore_gods_word', userLang)}
                </DSText>

                {/* Mode Indicator - Shows current mode in dev only */}
                {__DEV__ && (
                  <View style={{
                    position: 'absolute',
                    top: spacing.sm + 2,
                    right: spacing.sm + 2,
                    backgroundColor: 'rgba(8,10,24,0.84)',
                    borderWidth: 1,
                    borderColor: 'rgba(246,190,58,0.28)',
                    paddingHorizontal: spacing.sm,
                    paddingVertical: spacing.xs,
                    borderRadius: 12,
                  }}>
                    <DSText variant="micro" color={iqesColors.goldSoft} style={{ fontWeight: '600' }}>
                      {mode.toUpperCase()}
                    </DSText>
                  </View>
                )}
              </View>

              {/* Main Content Area */}
              <View style={{
                flex: 1,
                paddingHorizontal: spacing['2xl'],
                paddingVertical: spacing.lg,
                minHeight: 600,
              }}>
                <View style={{
                  flex: 1,
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  maxWidth: 420,
                  alignSelf: 'center',
                  width: '100%',
                  paddingTop: spacing.xl,
                }}>
                  {/* Conditional Rendering: Church History Subcategories OR Testament Options */}
                  {isChurchHistory ? (
                    // Render Church History Subcategories
                    loadingSubcategories ? (
                      <View style={{ padding: spacing['4xl'], alignItems: 'center' }}>
                        <DSText variant="body" color={'rgba(255,248,232,0.72)'}>
                          {t('loading', userLang) || 'Loading...'}
                        </DSText>
                      </View>
                    ) : subcategories.length === 0 ? (
                      <View style={{ padding: spacing['4xl'], alignItems: 'center' }}>
                        <DSText variant="body" color={'rgba(255,248,232,0.72)'} align="center">
                          {t('no_difficulties', userLang) || 'No options found.'}
                        </DSText>
                      </View>
                    ) : (
                      // Render subcategory cards
                      subcategories.map((subcat) => (
                        <TouchableOpacity
                          key={subcat.category_id}
                          onPress={() => handleSubcategorySelect(subcat.category_id, subcat.name)}
                          style={{
                            width: '100%',
                            marginBottom: spacing.lg,
                            backgroundColor: 'rgba(8,10,24,0.92)',
                            borderWidth: 1,
                            borderColor: 'rgba(246,190,58,0.34)',
                            shadowColor: iqesColors.gold,
                            borderRadius: radius.xl,
                            paddingVertical: spacing.xl,
                            paddingHorizontal: spacing.xl,
                            ...shadows.lg,
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                          activeOpacity={0.85}
                        >
                          {/* Icon Container */}
                          <View style={{
                            width: spacing['5xl'],
                            height: spacing['5xl'],
                            borderRadius: radius.md,
                            backgroundColor: 'rgba(246,190,58,0.12)',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: spacing.md + 2,
                            borderWidth: 1,
                            borderColor: 'rgba(246,190,58,0.38)',
                          }}>
                            <Ionicons
                              name="book-outline"
                              size={22}
                              color={iqesColors.goldSoft}
                            />
                          </View>

                          {/* Content */}
                          <View style={{ flex: 1 }}>
                            <DSText
                              variant="label"
                              color={iqesColors.ivory}
                              style={{ fontSize: 17, fontWeight: '700', marginBottom: 2 }}
                            >
                              {subcat.name}
                            </DSText>
                            {subcat.description && (
                              <DSText
                                variant="caption"
                                color={'rgba(255,248,232,0.72)'}
                                style={{ fontSize: 13, fontWeight: '500' }}
                              >
                                {subcat.description}
                              </DSText>
                            )}
                          </View>

                          {/* Arrow Indicator */}
                          <View style={{
                            width: 28,
                            height: 28,
                            borderRadius: radius.full,
                            backgroundColor: 'rgba(246,190,58,0.12)',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <Ionicons name="chevron-forward" size={14} color={iqesColors.goldBright} />
                          </View>
                        </TouchableOpacity>
                      ))
                    )
                  ) : (
                    // Render Testament Options (General & Books of the Bible)
                    <>
                      {/* General Mode Card */}
                      <TouchableOpacity
                        onPress={handleGeneralMode}
                        style={{
                          width: '100%',
                          marginBottom: spacing.xl,
                          backgroundColor: 'rgba(8,10,24,0.92)',
                          borderWidth: 1,
                          borderColor: 'rgba(246,190,58,0.34)',
                          shadowColor: iqesColors.gold,
                          borderRadius: radius.xl,
                          paddingVertical: spacing['2xl'],
                          paddingHorizontal: spacing['2xl'],
                          ...shadows.lg,
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                        activeOpacity={0.85}
                      >
                        {/* Icon Container */}
                        <View style={{
                          width: 56,
                          height: 56,
                          borderRadius: radius.lg,
                          backgroundColor: 'rgba(246,190,58,0.12)',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: spacing.lg,
                          borderWidth: 1,
                          borderColor: 'rgba(246,190,58,0.38)',
                        }}>
                          <Ionicons name="book-outline" size={26} color={iqesColors.goldSoft} />
                        </View>

                        {/* Content */}
                        <View style={{ flex: 1 }}>
                          <DSText
                            variant="title"
                            color={iqesColors.ivory}
                            style={{ marginBottom: spacing.xs }}
                          >
                            {t('general', userLang)}
                          </DSText>
                          <DSText
                            variant="label"
                            color={'rgba(255,248,232,0.72)'}
                            style={{ lineHeight: 18 }}
                          >
                            {t('general_description', userLang)}
                          </DSText>
                        </View>

                        {/* Arrow Indicator */}
                        <View style={{
                          width: spacing['3xl'],
                          height: spacing['3xl'],
                          borderRadius: radius.full,
                          backgroundColor: 'rgba(246,190,58,0.12)',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Ionicons name="chevron-forward" size={16} color={iqesColors.goldBright} />
                        </View>
                      </TouchableOpacity>

                      {/* Books of the Bible Card */}
                      <TouchableOpacity
                        onPress={handleBooksMode}
                        style={{
                          width: '100%',
                          backgroundColor: 'rgba(8,10,24,0.92)',
                          borderWidth: 1,
                          borderColor: 'rgba(246,190,58,0.34)',
                          shadowColor: iqesColors.gold,
                          borderRadius: radius.xl,
                          paddingVertical: spacing['2xl'],
                          paddingHorizontal: spacing['2xl'],
                          ...shadows.lg,
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                        activeOpacity={0.85}
                      >
                        {/* Icon Container */}
                        <View style={{
                          width: 56,
                          height: 56,
                          borderRadius: radius.lg,
                          backgroundColor: 'rgba(246,190,58,0.12)',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: spacing.lg,
                          borderWidth: 1,
                          borderColor: 'rgba(246,190,58,0.38)',
                        }}>
                          <Ionicons name="book" size={26} color={iqesColors.goldSoft} />
                        </View>

                        {/* Content */}
                        <View style={{ flex: 1 }}>
                          <DSText
                            variant="title"
                            color={iqesColors.ivory}
                            style={{ marginBottom: spacing.xs }}
                          >
                            {t('books_of_the_bible', userLang)}
                          </DSText>
                          <DSText
                            variant="label"
                            color={'rgba(255,248,232,0.72)'}
                            style={{ lineHeight: 18 }}
                          >
                            {t('books_focus_description', userLang)}
                          </DSText>
                        </View>

                        {/* Arrow Indicator */}
                        <View style={{
                          width: spacing['3xl'],
                          height: spacing['3xl'],
                          borderRadius: radius.full,
                          backgroundColor: 'rgba(246,190,58,0.12)',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Ionicons name="chevron-forward" size={16} color={iqesColors.goldBright} />
                        </View>
                      </TouchableOpacity>
                    </>
                  )}

                  {/* Footer text removed — per design philosophy: reduce text, increase visual meaning */}
                </View>
              </View>
            </View>
          </ScrollView>
        </ScrollView>
      </SafeAreaView>
    </View>
  );
};

export default UnifiedTestamentScreen;
